import { useEffect, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useEditMode,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/**
 * Sister Properties — curated nearby communities under the same management.
 *
 * There is no portfolio collection in Vibesites yet, so each community is a
 * fixed slot group. Editors set name, location, and Website URL; the image
 * generator pulls a card photo from that URL once the sister site is live.
 * Empty slots are hidden on the live site and shown as dashed placeholders
 * in edit mode so operators can see exactly where to click.
 */

// ─── Slot group definitions (one literal per community — the schema extractor
//     follows one level of module-level SlotGroupDefinition constants but not
//     factories, so we repeat the shape rather than build them in a loop) ─────

const community1Def: SlotGroupDefinition = {
  label: 'Community 1',
  description: 'Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: '',
      generator: {
        prompt:
          'Pull a representative community photo from this card’s Website URL — prefer the site’s hero, og:image, or main exterior of the building/grounds. Do not use this property’s own gallery. If the Website URL is empty, unreachable, or has no usable photo, fall back to the company media library and pick a photo whose title, filename, or tags name this card’s community. Never use a photo labeled for a different community. Title/name the chosen image with the community name so it is clear which property it belongs to. If nothing matches, leave empty.',
        context:
          'Card 1 of 6 on Sister Properties — card thumb. Source only this card’s Website URL field; do not reuse images from cards 2–6.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: '',
      generator: {
        prompt:
          'Accessibility alt text for this sister community card image. Always start with the community name (e.g. “<Community Name> — <what the photo shows>”) so the image is identifiable by property, then describe what the photo shows.',
        context:
          'Card 1 of 6 on Sister Properties. Image comes from this card’s Website URL — describe that photo only, not other sister communities.',
        length: 'short phrase',
      },
    },
    fallbackImage: {
      label: 'Fallback Image (Company Media)',
      type: 'image',
      defaultField: 'property_company_logo',
    },
    name: { label: 'Community Name', type: 'text', defaultValue: '' },
    location: { label: 'Location', type: 'text', defaultValue: '' },
    url: { label: 'Website URL', type: 'url', defaultValue: '' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const community2Def: SlotGroupDefinition = {
  label: 'Community 2',
  description: 'Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: '',
      generator: {
        prompt:
          'Pull a representative community photo from this card’s Website URL — prefer the site’s hero, og:image, or main exterior of the building/grounds. Do not use this property’s own gallery. If the Website URL is empty, unreachable, or has no usable photo, fall back to the company media library and pick a photo whose title, filename, or tags name this card’s community. Never use a photo labeled for a different community. Title/name the chosen image with the community name so it is clear which property it belongs to. If nothing matches, leave empty.',
        context:
          'Card 2 of 6 on Sister Properties — card thumb. Source only this card’s Website URL field; do not reuse images from cards 1, 3–6.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: '',
      generator: {
        prompt:
          'Accessibility alt text for this sister community card image. Always start with the community name (e.g. “<Community Name> — <what the photo shows>”) so the image is identifiable by property, then describe what the photo shows.',
        context:
          'Card 2 of 6 on Sister Properties. Image comes from this card’s Website URL — describe only this card’s photo, not cards 1 or 3–6.',
        length: 'short phrase',
      },
    },
    fallbackImage: {
      label: 'Fallback Image (Company Media)',
      type: 'image',
      defaultField: 'property_company_logo',
    },
    name: { label: 'Community Name', type: 'text', defaultValue: '' },
    location: { label: 'Location', type: 'text', defaultValue: '' },
    url: { label: 'Website URL', type: 'url', defaultValue: '' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const community3Def: SlotGroupDefinition = {
  label: 'Community 3',
  description: 'Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: '',
      generator: {
        prompt:
          'Pull a representative community photo from this card’s Website URL — prefer the site’s hero, og:image, or main exterior of the building/grounds. Do not use this property’s own gallery. If the Website URL is empty, unreachable, or has no usable photo, fall back to the company media library and pick a photo whose title, filename, or tags name this card’s community. Never use a photo labeled for a different community. Title/name the chosen image with the community name so it is clear which property it belongs to. If nothing matches, leave empty.',
        context:
          'Card 3 of 6 on Sister Properties — card thumb. Source only this card’s Website URL field; do not reuse images from cards 1–2 or 4–6.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: '',
      generator: {
        prompt:
          'Accessibility alt text for this sister community card image. Always start with the community name (e.g. “<Community Name> — <what the photo shows>”) so the image is identifiable by property, then describe what the photo shows.',
        context:
          'Card 3 of 6 on Sister Properties. Image comes from this card’s Website URL — describe only this card’s photo, not cards 1–2 or 4–6.',
        length: 'short phrase',
      },
    },
    fallbackImage: {
      label: 'Fallback Image (Company Media)',
      type: 'image',
      defaultField: 'property_company_logo',
    },
    name: { label: 'Community Name', type: 'text', defaultValue: '' },
    location: { label: 'Location', type: 'text', defaultValue: '' },
    url: { label: 'Website URL', type: 'url', defaultValue: '' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const community4Def: SlotGroupDefinition = {
  label: 'Community 4',
  description: 'Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: '',
      generator: {
        prompt:
          'Pull a representative community photo from this card’s Website URL — prefer the site’s hero, og:image, or main exterior of the building/grounds. Do not use this property’s own gallery. If the Website URL is empty, unreachable, or has no usable photo, fall back to the company media library and pick a photo whose title, filename, or tags name this card’s community. Never use a photo labeled for a different community. Title/name the chosen image with the community name so it is clear which property it belongs to. If nothing matches, leave empty.',
        context:
          'Card 4 of 6 on Sister Properties — card thumb. Source only this card’s Website URL field; do not reuse images from cards 1–3 or 5–6.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: '',
      generator: {
        prompt:
          'Accessibility alt text for this sister community card image. Always start with the community name (e.g. “<Community Name> — <what the photo shows>”) so the image is identifiable by property, then describe what the photo shows.',
        context:
          'Card 4 of 6 on Sister Properties. Image comes from this card’s Website URL — describe only this card’s photo, not cards 1–3 or 5–6.',
        length: 'short phrase',
      },
    },
    fallbackImage: {
      label: 'Fallback Image (Company Media)',
      type: 'image',
      defaultField: 'property_company_logo',
    },
    name: { label: 'Community Name', type: 'text', defaultValue: '' },
    location: { label: 'Location', type: 'text', defaultValue: '' },
    url: { label: 'Website URL', type: 'url', defaultValue: '' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const community5Def: SlotGroupDefinition = {
  label: 'Community 5',
  description: 'Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: '',
      generator: {
        prompt:
          'Pull a representative community photo from this card’s Website URL — prefer the site’s hero, og:image, or main exterior of the building/grounds. Do not use this property’s own gallery. If the Website URL is empty, unreachable, or has no usable photo, fall back to the company media library and pick a photo whose title, filename, or tags name this card’s community. Never use a photo labeled for a different community. Title/name the chosen image with the community name so it is clear which property it belongs to. If nothing matches, leave empty.',
        context:
          'Card 5 of 6 on Sister Properties — card thumb. Source only this card’s Website URL field; do not reuse images from cards 1–4 or 6.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: '',
      generator: {
        prompt:
          'Accessibility alt text for this sister community card image. Always start with the community name (e.g. “<Community Name> — <what the photo shows>”) so the image is identifiable by property, then describe what the photo shows.',
        context:
          'Card 5 of 6 on Sister Properties. Image comes from this card’s Website URL — describe only this card’s photo, not cards 1–4 or 6.',
        length: 'short phrase',
      },
    },
    fallbackImage: {
      label: 'Fallback Image (Company Media)',
      type: 'image',
      defaultField: 'property_company_logo',
    },
    name: { label: 'Community Name', type: 'text', defaultValue: '' },
    location: { label: 'Location', type: 'text', defaultValue: '' },
    url: { label: 'Website URL', type: 'url', defaultValue: '' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const community6Def: SlotGroupDefinition = {
  label: 'Community 6',
  description: 'Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: '',
      generator: {
        prompt:
          'Pull a representative community photo from this card’s Website URL — prefer the site’s hero, og:image, or main exterior of the building/grounds. Do not use this property’s own gallery. If the Website URL is empty, unreachable, or has no usable photo, fall back to the company media library and pick a photo whose title, filename, or tags name this card’s community. Never use a photo labeled for a different community. Title/name the chosen image with the community name so it is clear which property it belongs to. If nothing matches, leave empty.',
        context:
          'Card 6 of 6 on Sister Properties — card thumb. Source only this card’s Website URL field; do not reuse images from cards 1–5.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: '',
      generator: {
        prompt:
          'Accessibility alt text for this sister community card image. Always start with the community name (e.g. “<Community Name> — <what the photo shows>”) so the image is identifiable by property, then describe what the photo shows.',
        context:
          'Card 6 of 6 on Sister Properties. Image comes from this card’s Website URL — describe only this card’s photo, not cards 1–5.',
        length: 'short phrase',
      },
    },
    fallbackImage: {
      label: 'Fallback Image (Company Media)',
      type: 'image',
      defaultField: 'property_company_logo',
    },
    name: { label: 'Community Name', type: 'text', defaultValue: '' },
    location: { label: 'Location', type: 'text', defaultValue: '' },
    url: { label: 'Website URL', type: 'url', defaultValue: '' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

function hasText(value: string | null | undefined): boolean {
  return Boolean(value && value.trim());
}

function isFilled(community: Record<string, string | null>): boolean {
  return hasText(community.name) || hasText(community.url) || hasText(community.image);
}

function googleMapsUrl(query: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function CommunityCardBody({
  community,
  number,
  isEditing,
  filled,
}: {
  community: Record<string, string | null>;
  number: number;
  isEditing: boolean;
  filled: boolean;
}) {
  const href = (community.url || '').trim();
  const name = (community.name || '').trim();
  const location = (community.location || '').trim();
  const linkLabel = (community.label || 'Visit Community').trim();
  const target = community._target || '_blank';
  const rel = target === '_blank' ? 'noopener noreferrer' : undefined;
  const mapsHref = location ? googleMapsUrl(location) : '';
  const ownerLabel = name || `Community ${number}`;

  const [imageFailed, setImageFailed] = useState(false);
  const [fallbackFailed, setFallbackFailed] = useState(false);
  useEffect(() => setImageFailed(false), [community.image]);
  useEffect(() => setFallbackFailed(false), [community.fallbackImage]);

  const showPrimary = hasText(community.image) && !imageFailed;
  const showFallback = !showPrimary && hasText(community.fallbackImage) && !fallbackFailed;
  const primaryAlt = (community.alt || '').trim();
  const alt = showPrimary
    ? primaryAlt && primaryAlt.toLowerCase().includes(ownerLabel.toLowerCase())
      ? primaryAlt
      : primaryAlt
        ? `${ownerLabel} — ${primaryAlt}`
        : ownerLabel
    : `${ownerLabel} — company media`;

  return (
    <article
      className={`sister-card${filled ? '' : ' sister-card--empty'}`}
      aria-label={filled ? undefined : `Community ${number} — click to add details`}
    >
      <div className="sister-card-media" data-community={ownerLabel} title={ownerLabel}>
        {showPrimary ? (
          <VibesitesImage
            url={community.image || ''}
            alt={alt}
            className="sister-card-image"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            onError={() => setImageFailed(true)}
          />
        ) : showFallback ? (
          <VibesitesImage
            url={community.fallbackImage || ''}
            alt={alt}
            className="sister-card-image sister-card-image--fallback"
            objectFit="contain"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            onError={() => setFallbackFailed(true)}
          />
        ) : (
          <div className="sister-card-image-placeholder" aria-hidden="true" />
        )}
        {isEditing ? (
          <span className="sister-card-media-label">
            {ownerLabel}
            {showFallback ? ' · company media fallback' : ''}
          </span>
        ) : null}
      </div>

      <div className="sister-card-body">
        <h3 className="sister-card-name">
          {name || (isEditing ? `Community ${number}` : '')}
        </h3>
        {location ? (
          <a
            className="sister-card-location"
            href={mapsHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            {location}
          </a>
        ) : isEditing && !filled ? (
          <p className="sister-card-location sister-card-hint">Add city &amp; state</p>
        ) : null}

        {href ? (
          <a className="sister-card-link" href={href} target={target} rel={rel}>
            {linkLabel}
          </a>
        ) : isEditing ? (
          <span className="sister-card-link sister-card-link--muted">Add website URL</span>
        ) : null}
      </div>
    </article>
  );
}

export function SisterProperties() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const editMode = useEditMode();
  const isEditing = Boolean(editMode?.isEditMode);

  const community1 = useSlotGroup('sisterproperties.community1', community1Def);
  const community2 = useSlotGroup('sisterproperties.community2', community2Def);
  const community3 = useSlotGroup('sisterproperties.community3', community3Def);
  const community4 = useSlotGroup('sisterproperties.community4', community4Def);
  const community5 = useSlotGroup('sisterproperties.community5', community5Def);
  const community6 = useSlotGroup('sisterproperties.community6', community6Def);

  return (
    <section ref={sectionRef} className="reveal-fade-up sister-properties">
      <div className="sister-properties-container">
        <div className="sister-properties-grid">
          {/*
            EditableSlotGroup groupPath must be a string literal for the schema
            linter — keep each wrapper inline rather than mapping a shared path.
          */}
          {(isEditing || isFilled(community1)) && (
            <EditableSlotGroup groupPath="sisterproperties.community1" definition={community1Def}>
              <CommunityCardBody
                community={community1}
                number={1}
                isEditing={isEditing}
                filled={isFilled(community1)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community2)) && (
            <EditableSlotGroup groupPath="sisterproperties.community2" definition={community2Def}>
              <CommunityCardBody
                community={community2}
                number={2}
                isEditing={isEditing}
                filled={isFilled(community2)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community3)) && (
            <EditableSlotGroup groupPath="sisterproperties.community3" definition={community3Def}>
              <CommunityCardBody
                community={community3}
                number={3}
                isEditing={isEditing}
                filled={isFilled(community3)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community4)) && (
            <EditableSlotGroup groupPath="sisterproperties.community4" definition={community4Def}>
              <CommunityCardBody
                community={community4}
                number={4}
                isEditing={isEditing}
                filled={isFilled(community4)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community5)) && (
            <EditableSlotGroup groupPath="sisterproperties.community5" definition={community5Def}>
              <CommunityCardBody
                community={community5}
                number={5}
                isEditing={isEditing}
                filled={isFilled(community5)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community6)) && (
            <EditableSlotGroup groupPath="sisterproperties.community6" definition={community6Def}>
              <CommunityCardBody
                community={community6}
                number={6}
                isEditing={isEditing}
                filled={isFilled(community6)}
              />
            </EditableSlotGroup>
          )}
        </div>

        {isEditing ? (
          <p className="sister-properties-editor-note">
            Click a community card to add name, location, and website URL.
            Generate fills each card image from that community&apos;s website when the site is live,
            or from company media labeled with that community&apos;s name. If an image is missing or
            fails to load, the card shows its Fallback Image (company logo by default).
            Empty cards stay hidden on the live site.
          </p>
        ) : null}
      </div>

      <style>{`
        .sister-properties {
          padding: 3.5rem 0 4rem;
          background: #ffffff;
        }
        @media (min-width: 768px) {
          .sister-properties { padding: 5rem 0 5.5rem; }
        }

        .sister-properties-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .sister-properties-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .sister-properties-container { padding: 0 2rem; } }

        .sister-properties-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 640px) {
          .sister-properties-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (min-width: 1024px) {
          .sister-properties-grid { grid-template-columns: repeat(3, 1fr); gap: 1.75rem; }
        }

        .sister-card {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: #ffffff;
          border: 1px solid #e8e8ea;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
          transition: box-shadow 0.25s ease, border-color 0.25s ease;
        }
        .sister-card:hover {
          box-shadow: 0 8px 28px rgba(0, 0, 0, 0.08);
          border-color: #dddde1;
        }

        .sister-card--empty {
          border-style: dashed;
          box-shadow: none;
          background: #fafafa;
        }
        .sister-card--empty:hover {
          box-shadow: none;
          border-color: var(--vibe-color-primary);
        }

        .sister-card-media {
          position: relative;
          aspect-ratio: 4 / 3;
          background: #e8e8ea;
          overflow: hidden;
        }

        .sister-card-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .sister-card-image--fallback {
          padding: 2rem;
          box-sizing: border-box;
          background: #f4f4f6;
        }

        .sister-card-media-label {
          position: absolute;
          left: 0.6rem;
          bottom: 0.6rem;
          max-width: calc(100% - 1.2rem);
          padding: 0.2rem 0.55rem;
          border-radius: 999px;
          background: rgba(0, 0, 0, 0.65);
          color: #ffffff;
          font-family: var(--vibe-font-body);
          font-size: 0.7rem;
          line-height: 1.4;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          pointer-events: none;
        }

        .sister-card-image-placeholder {
          width: 100%;
          height: 100%;
          background:
            linear-gradient(135deg, #ececef 0%, #e2e3e6 50%, #ececef 100%);
        }

        .sister-card-body {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          padding: 1.25rem 1.35rem 1.5rem;
          flex: 1;
        }

        .sister-card-name {
          font-family: var(--vibe-font-heading);
          font-size: 1.125rem;
          font-weight: 700;
          line-height: 1.3;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: var(--vibe-color-text);
          margin: 0;
        }

        .sister-card-location {
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.5;
          color: var(--vibe-color-text-muted);
          margin: 0;
          text-decoration: none;
        }
        a.sister-card-location:hover {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        .sister-card-hint {
          font-style: italic;
        }

        .sister-card-link {
          display: inline-flex;
          align-items: center;
          margin-top: auto;
          padding-top: 0.85rem;
          font-family: var(--vibe-font-heading);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--vibe-color-primary);
          text-decoration: none;
        }
        .sister-card-link:hover {
          text-decoration: underline;
        }
        .sister-card-link--muted {
          color: var(--vibe-color-text-muted);
          font-style: italic;
          text-transform: none;
          letter-spacing: 0;
          font-family: var(--vibe-font-body);
          font-weight: 400;
          font-size: 0.85rem;
        }

        .sister-properties-editor-note {
          text-align: center;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          line-height: 1.6;
          color: var(--vibe-color-text-muted);
          margin: 1.75rem 0 0;
          font-style: italic;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SisterProperties',
  displayName: 'Sister Properties',
  tokens: {},
  slotGroups: {
    'sisterproperties.community1': community1Def,
    'sisterproperties.community2': community2Def,
    'sisterproperties.community3': community3Def,
    'sisterproperties.community4': community4Def,
    'sisterproperties.community5': community5Def,
    'sisterproperties.community6': community6Def,
  },
};

(SisterProperties as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default SisterProperties;
